import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// ScrollRestoration — manual scroll save/restore for BrowserRouter.
//
// React Router v6's built-in <ScrollRestoration> only works with the
// data-router (createBrowserRouter + RouterProvider). This app uses
// the classic <BrowserRouter>, so we implement the same UX by hand:
//
//   - On every render where the location changes, decide whether we're
//     entering a new location ("PUSH") or revisiting an old one
//     ("POP" — i.e. browser back/forward button).
//   - For PUSH: scroll to the top.
//   - For POP: read the previously saved scrollY for this getKey(loc)
//     and scroll there. Falls back to 0 if no snapshot exists.
//   - On every scroll, debounce-save the current scrollY against
//     getKey(loc) in sessionStorage. sessionStorage (not localStorage)
//     because scroll position is tab-scoped — a fresh tab opening the
//     same URL should start at top.
//
// `getKey` defaults to `loc.pathname + loc.search`. That means each
// unique filter combination on Opportunities ("?state=NV&page=3") gets
// its own remembered scroll position, which is exactly the issue-#126
// behavior the user described.
//
// `STORAGE_KEY` is exported so tests can clear it between cases.

export const SCROLL_STORAGE_KEY = 'landai_scroll_positions';

const DEFAULT_GET_KEY = (loc) => loc.pathname + loc.search;

// Cap the number of remembered positions so a long browsing session
// can't blow sessionStorage's per-origin quota (~5 MB on most browsers).
// LRU-style: when we overflow, drop the oldest entry.
const MAX_ENTRIES = 100;

function readSnapshot() {
  try {
    const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
}

function writeSnapshot(snap) {
  try {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(snap));
  } catch { /* quota or security error — non-fatal */ }
}

export function saveScroll(key, scrollY) {
  const snap = readSnapshot();
  // Move-to-end LRU: delete then re-insert so the most recently used
  // key sits at the tail.
  delete snap[key];
  snap[key] = scrollY;
  const keys = Object.keys(snap);
  if (keys.length > MAX_ENTRIES) {
    // Drop the head (oldest entries) until under cap.
    const toDelete = keys.slice(0, keys.length - MAX_ENTRIES);
    for (const k of toDelete) delete snap[k];
  }
  writeSnapshot(snap);
}

export function loadScroll(key) {
  const snap = readSnapshot();
  return typeof snap[key] === 'number' ? snap[key] : null;
}

// React component. Mount once at the top of the tree (App.js, inside
// the Router). It renders nothing.
export default function ScrollRestoration({ getKey = DEFAULT_GET_KEY } = {}) {
  const location = useLocation();
  const navigationType = useNavigationType(); // 'PUSH' | 'POP' | 'REPLACE'

  // The last key we observed, so the scroll listener can write to the
  // right entry even mid-route-transition. Using a ref means the
  // listener doesn't have to be torn down and re-attached on every
  // route change.
  const currentKeyRef = useRef(getKey(location));

  // The pathname from the previous location change, so we can tell an
  // in-place query-string REPLACE (same pathname) apart from a REPLACE
  // that actually changes the destination (e.g. an auth redirect). See
  // the REPLACE handling below (#978).
  const prevPathnameRef = useRef(location.pathname);

  // Apply the scroll on every location change.
  useEffect(() => {
    const key = getKey(location);
    currentKeyRef.current = key;

    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (navigationType === 'POP') {
      const y = loadScroll(key);
      // Use rAF so the browser has painted the new route's content
      // before we try to scroll into it — otherwise tall pages would
      // clip to scrollHeight=0 and the scroll would be a no-op.
      const id = window.requestAnimationFrame(() => {
        window.scrollTo(0, y == null ? 0 : y);
      });
      return () => window.cancelAnimationFrame(id);
    }

    // A REPLACE that keeps the same pathname is an in-place URL update,
    // not a real navigation — e.g. SearchPage's debounced query-string
    // sync as the map pans/zooms, or Compare's ?ids= write. Forcing
    // scroll-to-top on these is wrong UX (it yanks the user back up on
    // every filter tweak) and, on iOS Safari, it drives a self-sustaining
    // reset/flicker loop (#978): scrollTo(0,0) expands the address bar →
    // the visual viewport shrinks → Leaflet's default trackResize fires
    // invalidateSize → `moveend` → a fresh mapView object → another
    // debounced REPLACE → another scrollTo(0,0)… forever. Preserve the
    // current scroll position instead so the loop can't start.
    if (navigationType === 'REPLACE' && location.pathname === prevPathname) {
      return undefined;
    }

    // PUSH, or a REPLACE that changes the pathname (e.g. the /login
    // redirect): a genuinely new destination — start from the top. This
    // matches the default browser behavior on link clicks.
    window.scrollTo(0, 0);
    return undefined;
  }, [location, navigationType, getKey]);

  // Persist scrollY whenever the user scrolls. Debounce so we don't
  // hammer sessionStorage on a fast scroll wheel.
  useEffect(() => {
    let pending = null;
    const onScroll = () => {
      if (pending != null) return;
      pending = window.setTimeout(() => {
        pending = null;
        saveScroll(currentKeyRef.current, window.scrollY);
      }, 100);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Save once on mount too — guarantees the key exists in the
    // snapshot for the current location even if the user never scrolls
    // before navigating away. (Without this, a click-through with no
    // scrolling would later restore as "no snapshot → scroll to 0",
    // which is fine, but a 0 entry makes telemetry-style debugging
    // easier.)
    saveScroll(currentKeyRef.current, window.scrollY);
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (pending != null) window.clearTimeout(pending);
      // Final flush on unmount.
      saveScroll(currentKeyRef.current, window.scrollY);
    };
  }, []);

  return null;
}
